import { IFinancialIndicator } from "api/interfaces/megaERP";
import { decimal } from "utils/DecimalPrecision";
import {
  EContractStatus,
  EManagementType,
  ETechnicalRetentionType,
} from "../enums";
import {
  IContractPaymentCondition,
  IContractTaxes,
  ICustomPropsDefinitions,
  IDocumentErp,
  IExecutiveBudget,
  IIndicatorQuotation,
  ITechnicalRetention,
  IUsers,
} from "../interfaces";
import DevelopmentModel from "./DevelopmentModel";

/**
 * Model de Contrato
 * @category Contracts
 * @hideconstructor
 */

class ContractModel {
  /** ID do contrato. */
  public readonly uid: string;
  /** Nome do contrato. */
  public name: string;
  /** Data de criação do contrato */
  public date: Date;
  /** Dados do projeto do contrato. Primeiramente os dados do proejto são salvos no contrato
   * mas no futuro a ideia é integrar com o Orçamento e criar uma Switch.
   */
  public development: DevelopmentModel;
  /** Objeto com os usuários e suas permissões de acesso ao contrato. */
  public users: IUsers;
  /** Status do contrato. */
  public status: EContractStatus;
  /** Tipo do regime de execução do contrato. */
  public managementType: EManagementType;
  /** Valor total do contrato. */
  public total: number;
  /** Soma das medições do contrato. */
  public measuredTotal: number;
  /** Soma das medições faturadas do contrato. */
  public billingTotal: number;
  /** Flag que sinaliza que os valores do contrato foram alterados após importação. */
  public hasChanged: boolean;
  /** Data de criação do registro. */
  public readonly createdAt: Date;
  /** Data de modificação do registro. */
  public modifiedAt: Date;
  /** Dados de configuração da retenção técnica aplica ao contrato. */
  public technicalRetention?: ITechnicalRetention;
  /** Dados de configuração dos impostos aplicados ao contrato. */
  public taxes?: IContractTaxes;
  /** ID da filial do ERP Mega vinculado ao contrato. */
  public branchId?: number;
  /** Dados da condição de pagamento do ERP Mega vinculado ao contrato. */
  public paymentCondition?: IContractPaymentCondition;
  /** Dados do tipo de documento do ERP Mega vinculado ao contrato. */
  public documentType?: IDocumentErp;
  /** Dados do centro de custo do ERP Mega vinculado ao contrato. */
  public costCenter?: IDocumentErp;
  /** Dados do projeto do ERP Mega vinculado ao contrato. */
  public projectErp?: IDocumentErp;
  /** Dados do indicador financeiro do ERP Mega vinculado ao contrato. */
  public financialIndicator?: IFinancialIndicator;
  /** Dados do indicador e cotação utilizados para o reajuste do contrato. */
  public readjustmentQuotation?: IIndicatorQuotation;
  /** IDs dos anexos vinculados ao contrato. */
  public attachments: string[];
  /** Imagem de Capa do contrato. */
  public coverImageUrl?: string;
  /** Dados do orçamento executivo do ERP Mega vinculado ao contrato. */
  private _executiveBudget?: IExecutiveBudget;
  /** Definições de propriedades customizadas do contrato. */
  private _customProperties: ICustomPropsDefinitions[] = [];

  constructor(
    uid: string,
    development: DevelopmentModel,
    date?: Date,
    name: string = "",
    users: IUsers = {},
    status: EContractStatus = EContractStatus.AGUARDANDO,
    managementType: EManagementType = EManagementType.GLOBAL,
    total: number = 0,
    measuredTotal: number = 0,
    billingTotal: number = 0,
    hasChanged: boolean = false,
    createdAt = new Date(),
    modifiedAt = new Date(),
    technicalRetention?: ITechnicalRetention,
    taxes?: IContractTaxes,
    branchId?: number,
    paymentCondition?: IContractPaymentCondition,
    documentType?: IDocumentErp,
    costCenter?: IDocumentErp,
    projectErp?: IDocumentErp,
    executiveBudget?: IExecutiveBudget,
    financialIndicator?: IFinancialIndicator,
    readjustmentQuotation?: IIndicatorQuotation,
    attachments: string[] = [],
    coverImageUrl: string = ""
  ) {
    this.uid = uid;
    this.name = name;
    this.date = date || createdAt;
    this.development = development;
    this.users = users;
    this.status = status;
    this.managementType = managementType;
    this.total = total;
    this.measuredTotal = measuredTotal;
    this.billingTotal = billingTotal;
    this.hasChanged = hasChanged;
    this.createdAt = createdAt;
    this.modifiedAt = modifiedAt;
    this.taxes = taxes;
    this.branchId = branchId;
    this.paymentCondition = paymentCondition;
    this.documentType = documentType;
    this.costCenter = costCenter;
    this.projectErp = projectErp;
    this.financialIndicator = financialIndicator;
    this.readjustmentQuotation = readjustmentQuotation;
    this._executiveBudget = executiveBudget;
    this.attachments = attachments;
    this.coverImageUrl = coverImageUrl;

    /** Trata os antigos projetos que tinham o valor de technicalRetention como número */
    if (technicalRetention) {
      this.technicalRetention =
        typeof technicalRetention === "number"
          ? {
              percentual: technicalRetention,
              type: ETechnicalRetentionType.MEASUREMENT,
            }
          : technicalRetention;
    }
  }

  /** Retorna o ID do usuário proprietário do Contrato. */
  get ownerUserId(): string | undefined {
    return Object.keys(this.users).find((id) => this.users[id] === 0);
  }

  /** Retorna a lista de IDs dos usuários compartilhados do Contrato. */
  get sharedUsersIds(): string[] {
    return Object.keys(this.users).filter((id) => this.users[id] !== 0);
  }

  /** Retorna uma cópia dos dados do Orçamento executivo. */
  get executiveBudget(): IExecutiveBudget | undefined {
    return this._executiveBudget ? { ...this._executiveBudget } : undefined;
  }

  /** Retorna a lista de propriedades customizadas do contrato. */
  get customProperties(): ICustomPropsDefinitions[] {
    return [...this._customProperties];
  }

  /** Retorna o saldo restante do contrato. */
  getBalanceTotal(): number {
    return decimal(this.total).subtract(this.measuredTotal).toNumber();
  }

  /** Retorna a porcentagem do total faturado do contrato. */
  getBillingPercentual(): number {
    const totalRounded = decimal(this.total, 2).round();
    const billingPercentual = decimal(this.billingTotal, 2)
      .dividedBy(totalRounded)
      .toNumber();

    return decimal(billingPercentual * 100, 2).round();
  }

  /** Retorna se o contrato possui anexos. */
  hasAttachments() {
    return this.attachments.length > 0;
  }

  /** Adiciona o ID de usuário a lista de usuários com permissão
   * do contrato.
   */
  addUserId(uid: string, owner: boolean = false): void {
    if (this.users[uid]) {
      return;
    }

    this.users[uid] = owner ? 0 : 1;
  }

  /** Retorna um objeto com os usuários do contrato mesclados com os usuários
   * passados por parâmetro. Não afeta os dados do contrato!
   */
  mergeContractUsersWith(usersIds: string[]): IUsers {
    const newUsers = { ...this.users };
    usersIds.forEach((id) => {
      if (newUsers[id]) return;
      newUsers[id] = 1;
    });

    return newUsers;
  }

  /** Verifica se o ID de usuário informado existe no contrato. */
  hasUserId(uid: string): boolean {
    return Object.keys(this.users).includes(uid);
  }

  /** Setter para propriedade de users do contrato. */
  setUsers(newUsers: IUsers) {
    this.users = newUsers;
  }

  /** Setter para propriedade de executiveBudget do contrato. */
  setExecutiveBudget(branchId: number, budgetId: string) {
    this._executiveBudget = {
      id: budgetId,
      branchId,
    };
  }

  /** Setter para propriedade de customProperties do contrato. */
  setCustomProperties(props: ICustomPropsDefinitions[]) {
    this._customProperties = props;
  }

  /** Atualiza as propriedades do contrato com os valores do contrato
   * passado por parâmetro.
   */
  copyFrom(otherContract: ContractModel) {
    if (!otherContract) return;

    this.name = otherContract.name;
    this.date = otherContract.date;
    this.development = otherContract.development;
    this.users = otherContract.users;
    this.status = otherContract.status;
    this.managementType = otherContract.managementType;
    this.total = otherContract.total;
    this.measuredTotal = otherContract.measuredTotal;
    this.billingTotal = otherContract.billingTotal;
    this.hasChanged = otherContract.hasChanged;
    this.modifiedAt = otherContract.modifiedAt;
    this.technicalRetention = otherContract.technicalRetention;
    this.taxes = otherContract.taxes;
    this.branchId = otherContract.branchId;
    this.paymentCondition = otherContract.paymentCondition;
    this.documentType = otherContract.documentType;
    this.costCenter = otherContract.costCenter;
    this.projectErp = otherContract.projectErp;
    this.financialIndicator = otherContract.financialIndicator;
    this.readjustmentQuotation = otherContract.readjustmentQuotation;
    this._executiveBudget = otherContract.executiveBudget;
    this.attachments = otherContract.attachments;
    this.coverImageUrl = otherContract.coverImageUrl;
  }

  /** Retorna um objeto com os dados que seram salvos no banco de dados. */
  toSave() {
    return {
      name: this.name,
      date: this.date,
      development: { ...this.development },
      users: this.users,
      status: this.status,
      managementType: this.managementType,
      total: this.total,
      measuredTotal: this.measuredTotal,
      billingTotal: this.billingTotal,
      hasChanged: this.hasChanged,
      createdAt: this.createdAt,
      modifiedAt: this.modifiedAt,
      technicalRetention: this.technicalRetention,
      taxes: this.taxes,
      branchId: this.branchId,
      paymentCondition: this.paymentCondition && { ...this.paymentCondition },
      documentType: this.documentType && { ...this.documentType },
      costCenter: this.costCenter && { ...this.costCenter },
      projectErp: this.projectErp && { ...this.projectErp },
      executiveBudget: this.executiveBudget && { ...this.executiveBudget },
      financialIndicator: this.financialIndicator && {
        ...this.financialIndicator,
      },
      readjustmentQuotation: this.readjustmentQuotation && {
        ...this.readjustmentQuotation,
      },
      attachments: this.attachments,
      coverImageUrl: this.coverImageUrl,
    };
  }

  /** Cria uma instância de ContractModel a partir dos dados que vieram do
   * banco de dados.
   */
  static create(uid: string, data: Partial<ContractModel>): ContractModel {
    const dev = data.development;
    const development = new DevelopmentModel(
      dev?.name,
      dev?.customer,
      dev?.local,
      dev?.customerId,
      dev?.createdAt,
      dev?.modifiedAt
    );

    return new ContractModel(
      uid,
      development,
      data.date,
      data.name,
      data.users,
      data.status,
      data.managementType,
      data.total,
      data.measuredTotal,
      data.billingTotal,
      data.hasChanged,
      data.createdAt,
      data.modifiedAt,
      data.technicalRetention,
      data.taxes,
      data.branchId,
      data.paymentCondition,
      data.documentType,
      data.costCenter,
      data.projectErp,
      data.executiveBudget,
      data.financialIndicator,
      data.readjustmentQuotation,
      data.attachments,
      data.coverImageUrl
    );
  }
}

export default ContractModel;
